/**
 * «Cambió el plano» (OPUS ci, idea 4), modelos IFC y dibujos DWG/DXF: subir la revisión nueva del plano, compararla con la hoja y
 * aplicarla (o volver a como estaba). Solo staff. Sin IA, $0: las dos revisiones se miden en el navegador con la
 * medición de la pantalla del plano (`medirIfcSinVisor`, una detrás de otra) y la regla de qué cambia vive en
 * @supernova/shared (`compararRevisiones`). El servidor guarda la comparación, la valida y la aplica.
 *
 * Pasos: 1) elegir el archivo nuevo (o retomar la revisión abierta); 2) medir las dos y comparar; 3) ver el
 * resultado (Antes · Ahora · Diferencia, renglón por renglón, piezas nuevas sin concepto, lo que no se aplica
 * solo); 4) «Aplicar a mi hoja» con confirmación, «Volver a como estaba» o «Descartar revisión».
 *
 * Vista previa en 3D: UN solo visor con «Antes · Ahora» (dos visores IFC duplicarían la memoria): en «Antes» se pinta lo
 * quitado y lo modificado; en «Ahora», lo nuevo y lo modificado. DWG/DXF: sin vista previa; se compara por capa y planta
 * con el inventario que el servidor ya guarda (la revisión se lee en el servidor y se compara al terminar).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  ProgressBar,
  Spinner,
  Tab,
  TabList,
  Toast,
  ToastTitle,
  useToastController,
} from '@fluentui/react-components';
import { ArrowUpload16Regular } from '@fluentui/react-icons';
import { diffEntidades, elementosDeSeleccion, factorDeEscala, pareceOtroPlano, type ResultadoDiffEntidades } from '@supernova/shared';
import type { CambioDePieza, CambioDeVista, ComparacionDePlanos, ImpactoDeRenglon, PlanoRenglon, PlanoRevisionResumen, TipoCambioPieza } from '@supernova/shared';
import { ApiClientError } from '../../api/client.js';
import { getParametrosCambioDePlano, getParametrosMedicionIfc } from '../../api/calibracion.js';
import { getProject, uploadFiles } from '../../api/projects.js';
import { fetchPlanoDxf } from '../../api/planos.js';
import {
  aplicarRevisionPlano,
  crearRevisionPlano,
  descartarRevisionPlano,
  guardarComparacionPlano,
  listarRevisionesPlano,
  obtenerRevisionPlano,
  revertirRevisionPlano,
  usarPropuestaRevision,
} from '../../api/planoRevisiones.js';
import {
  coloresDeLaVista,
  compararRevisionIfc,
  fechaDeRevision,
  filaDePieza,
  importeDeHoja,
  importeDespues,
  ordenarImpacto,
  piezaDeFila,
  resumenDeAplicar,
} from '../../lib/compararRevisionIfc.js';
import { medirIfcSinVisor, textoDeAvanceDeMedir, type AvanceDeMedir } from '../../lib/medirIfcSinVisor.js';
import { cambiosDeVistasDeRevision, compararRevisionDwg } from '../../lib/compararRevisionDwg.js';
import { leerPlanosDeLaRevisionDwg } from '../../lib/leerRevisionDwg.js';
import { firmasDeDxf } from '../../lib/firmasDxf.js';
import { FieldHelp } from '../FieldHelp.js';
import { IfcViewer3D } from '../IfcViewer3D.js';
import PlanoViewerMlightcad from '../PlanoViewerMlightcad.js';
import { GLOBAL_TOASTER_ID } from '../ThemedProvider.js';

interface Props {
  /** El formato del plano de la hoja: la revisión tiene que ser del mismo. */
  formato: 'ifc' | 'dwg';
  abierto: boolean;
  onCerrar: () => void;
  projectId: string;
  /** El archivo que la hoja tiene HOY. */
  fileId: string;
  formatoPrecio: (n: number) => string;
  /**
   * La hoja cambió (aplicar, volver, usar una cantidad). `archivo` = el archivo que la hoja tiene ahora, si cambió
   * (al aplicar pasa al nuevo; al volver, al de antes): la pantalla lo abre.
   */
  onHojaCambiada: (archivo: string | null) => void;
}

const ABIERTAS = new Set(['subida', 'comparada']);
const fmtCant = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 3 });
const unidadBonita = (u: string | null | undefined) => (u ?? '').replace(/m2$/i, 'm²').replace(/m3$/i, 'm³');
const corto = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const esDelFormato = (nombre: string, formato: 'ifc' | 'dwg') => (formato === 'ifc' ? /\.ifc$/i : /\.(dwg|dxf)$/i).test(nombre.trim());

type Tono = 'ok' | 'bad' | 'warn' | 'accent' | 'neutral';
function Pastilla({ texto, tono, title }: { texto: string; tono: Tono; title?: string }) {
  const color = tono === 'neutral' ? 'var(--text-secondary)' : `var(--${tono})`;
  const fondo = tono === 'neutral' ? 'var(--bg-raised)' : `var(--${tono}-weak)`;
  return (
    <span
      title={title}
      style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 600, padding: '1px 7px', borderRadius: 10, color, background: fondo, border: `1px solid ${color}`, whiteSpace: 'nowrap' }}
    >
      {texto}
    </span>
  );
}

const PASTILLA_RENGLON: Record<ImpactoDeRenglon['estado'], { texto: string; tono: Tono }> = {
  cambia: { texto: 'Cambió de medida', tono: 'warn' },
  ya_no_esta: { texto: 'Ya no está', tono: 'bad' },
  revisar: { texto: 'Revisar', tono: 'warn' },
  igual: { texto: 'Sin cambio', tono: 'neutral' },
  no_se_recalcula: { texto: 'No cambia con el plano', tono: 'neutral' },
};
const PASTILLA_PIEZA: Record<TipoCambioPieza, { texto: string; tono: Tono }> = {
  agregada: { texto: 'Nuevo', tono: 'ok' },
  quitada: { texto: 'Ya no está', tono: 'bad' },
  cambio_medida: { texto: 'Cambió de medida', tono: 'warn' },
  cambio_tipo: { texto: 'Cambió de tipo', tono: 'accent' },
  movida: { texto: 'Se movió', tono: 'accent' },
};
const NO_ES_SEGURO = 'No es seguro que sea la misma pieza';
/** Referencia fija: el visor del dibujo repinta todo si `renglones` cambia de referencia. */
const SIN_RENGLONES: PlanoRenglon[] = [];

/** El mensaje de un error de la API para la persona (los 409 ya traen el suyo, en español). */
function mensajeDe(e: unknown, respaldo: string): string {
  if (e instanceof ApiClientError) {
    if (e.code === 'REVISION_DESACTUALIZADA' && !/aplicar/.test(e.message)) return 'Tu hoja cambió después de comparar: vuelve a comparar. Tu hoja no cambió.';
    return e.message || respaldo;
  }
  if (e instanceof Error && e.message) return e.message;
  return respaldo;
}

export function CambioDePlano({ formato, abierto, onCerrar, projectId, fileId, formatoPrecio: pesos, onHojaCambiada }: Props): JSX.Element | null {
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);
  const [cargando, setCargando] = useState(false);
  const [revision, setRevision] = useState<PlanoRevisionResumen | null>(null);
  const [comparacion, setComparacion] = useState<ComparacionDePlanos | null>(null);
  const [renglones, setRenglones] = useState<PlanoRenglon[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [desactualizada, setDesactualizada] = useState(false);
  const [trabajando, setTrabajando] = useState<string | null>(null);
  const [subida, setSubida] = useState<number | null>(null);
  // `servidor` = DWG: se espera a que el servidor termine de leer la revisión (no hay nada que medir en el navegador).
  const [midiendo, setMidiendo] = useState<{ cual: 1 | 2; avance: AvanceDeMedir | null; servidor?: boolean } | null>(null);
  // DWG: avisos de la comparación y qué cambió en las plantas (no van a la comparación guardada).
  const [avisosDwg, setAvisosDwg] = useState<string[]>([]);
  const [cambiosVista, setCambiosVista] = useState<CambioDeVista[]>([]);
  const [confirmar, setConfirmar] = useState<'aplicar' | 'revertir' | 'descartar' | null>(null);
  const [pestana, setPestana] = useState<'presupuesto' | 'modelo'>('presupuesto');
  // DWG: lo que se quiere ver en el dibujo. `foco` = las capas y bloques de UN concepto (el resto se atenúa).
  // `n` hace que cada pedido cuente aunque se repita.
  const [foco, setFoco] = useState<{ n: number; titulo: string; capas: string[]; bloques: string[] } | null>(null);
  const contadorVista = useRef(0);
  const entrada = useRef<HTMLInputElement>(null);
  // DWG: el dibujo nuevo parece OTRO plano (casi nada coincide). Se le pregunta si quiere continuar; esto recuerda que dijo que sí.
  const [seguirAunqueSeaOtro, setSeguirAunqueSeaOtro] = useState(false);
  // El umbral de «parece otro plano» (Calibración): % de piezas de AMBOS lados sin pareja.
  const [umbralOtro, setUmbralOtro] = useState<number | null>(null);
  useEffect(() => {
    if (!abierto) return;
    let vivo = true;
    getParametrosCambioDePlano()
      .then((pc) => vivo && setUmbralOtro(pc.cambio_dwg_desplazado_pct))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [abierto]);
  // DWG: el diff ENTIDAD POR ENTIDAD de los dos dibujos (solo para mostrar). Se lee cada DXF una vez, se sacan las firmas y se
  // comparan; el visor recibe los mismos bytes (no se baja dos veces). Vive aquí (no en la pestaña) para poder avisar desde el
  // principio si el dibujo nuevo se parece muy poco al de la hoja. Si algo falla, se cae al resalte por capa y bloque.
  const [diffDwg, setDiffDwg] = useState<ResultadoDiffEntidades | null>(null);
  const [calculandoDiff, setCalculandoDiff] = useState(false);
  const [bytesDwg, setBytesDwg] = useState<{ a: ArrayBuffer; b: ArrayBuffer } | null>(null);
  const [centrosDwg, setCentrosDwg] = useState<{ a: ReadonlyMap<string, { x: number; y: number }>; b: ReadonlyMap<string, { x: number; y: number }> } | null>(null);
  const idAnterior = revision?.fileAnteriorId;
  useEffect(() => setSeguirAunqueSeaOtro(false), [revision?.id]);
  const idNuevo = revision?.fileNuevoId;
  const hayComparacion = comparacion != null;
  const parecePocoOtro =
    formato === 'dwg'
      ? !!diffDwg?.desplazado
      : umbralOtro != null && pareceOtroPlano(comparacion?.parecido, umbralOtro);
  const cosa = formato === 'ifc' ? 'modelo' : 'dibujo';
  useEffect(() => {
    setDiffDwg(null);
    setBytesDwg(null);
    setCentrosDwg(null);
    if (formato !== 'dwg' || !abierto || !idAnterior || !idNuevo || !hayComparacion) {
      setCalculandoDiff(false);
      return;
    }
    const ctrl = new AbortController();
    let vivo = true;
    (async () => {
      try {
        setCalculandoDiff(true);
        const [pc, leidos] = await Promise.all([
          getParametrosCambioDePlano(),
          leerPlanosDeLaRevisionDwg(projectId, idAnterior, idNuevo, { esperar: false, signal: ctrl.signal }),
        ]);
        const factor = factorDeEscala(leidos.a.inventario, leidos.a.elegida ?? null).factor ?? 1;
        const [ba, bb] = await Promise.all([fetchPlanoDxf(projectId, idAnterior), fetchPlanoDxf(projectId, idNuevo)]);
        const fa = await firmasDeDxf(ba, factor, { signal: ctrl.signal });
        const fb = await firmasDeDxf(bb, factor, { signal: ctrl.signal });
        const r = diffEntidades(fa, fb, { movidaM: pc.cambio_movida_m, emparejarM: pc.cambio_emparejar_distancia_m, desplazadoPct: pc.cambio_dwg_desplazado_pct });
        if (!vivo) return;
        const centros = (fs: typeof fa) => new Map(fs.filter((x) => x.puntos[0]).map((x) => [x.handle, { x: x.puntos[0]![0] / factor, y: x.puntos[0]![1] / factor }] as const));
        setBytesDwg({ a: ba, b: bb });
        setCentrosDwg({ a: centros(fa), b: centros(fb) });
        setDiffDwg(r);
      } catch {
        /* sin diff: el diálogo cae al resalte por capa y bloque */
      } finally {
        if (vivo) setCalculandoDiff(false);
      }
    })();
    return () => {
      vivo = false;
      ctrl.abort();
    };
  }, [formato, abierto, projectId, idAnterior, idNuevo, hayComparacion]);
  // Planos del mismo formato que ya están en el proyecto: se puede elegir uno como revisión sin volver a subirlo.
  const [existentes, setExistentes] = useState<{ id: string; nombre: string }[]>([]);
  const [elegidoExistente, setElegidoExistente] = useState('');
  const cancelar = useRef<AbortController | null>(null);

  const avisar = useCallback(
    (texto: string, intent: 'success' | 'error' = 'success') =>
      dispatchToast(
        <Toast>
          <ToastTitle>{texto}</ToastTitle>
        </Toast>,
        { intent, timeout: intent === 'success' ? 3000 : 6000 },
      ),
    [dispatchToast],
  );

  /** Lee las revisiones de la hoja y decide qué mostrar: la abierta, la aplicada (si se puede volver) o subir. */
  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const r = await listarRevisionesPlano(projectId, fileId);
      setRenglones(r.renglones);
      const abierta = r.revisiones.find((x) => ABIERTAS.has(x.estado));
      const aplicada = r.revisiones.find((x) => x.estado === 'aplicada' && x.puedeRevertir && x.fileNuevoId === fileId);
      const actual = abierta ?? aplicada ?? null;
      setRevision(actual);
      setDesactualizada(false);
      if (actual && actual.estado !== 'subida') {
        const d = await obtenerRevisionPlano(projectId, fileId, actual.id);
        setComparacion(d.comparacion);
        if (formato === 'dwg' && d.comparacion) {
          try {
            const [pc, leidos] = await Promise.all([getParametrosCambioDePlano(), leerPlanosDeLaRevisionDwg(projectId, actual.fileAnteriorId, actual.fileNuevoId, { esperar: false })]);
            setCambiosVista(cambiosDeVistasDeRevision({ a: leidos.a, b: leidos.b, parametros: pc }));
          } catch {
            setCambiosVista([]);
          }
        }
      } else setComparacion(null);
    } catch (e) {
      setError(mensajeDe(e, 'No se pudieron leer las revisiones de este plano.'));
    } finally {
      setCargando(false);
    }
  }, [projectId, fileId, formato]);

  useEffect(() => {
    if (!abierto) return;
    setConfirmar(null);
    void cargar();
  }, [abierto, cargar]);

  // Los planos del mismo formato que ya subiste a este proyecto (menos el de tu hoja).
  useEffect(() => {
    if (!abierto) return;
    let vivo = true;
    getProject(projectId)
      .then((r) => {
        if (!vivo) return;
        setExistentes(
          r.files
            .filter((f) => f.id !== fileId && esDelFormato(f.originalName, formato))
            .map((f) => ({ id: f.id, nombre: f.originalName }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
        );
      })
      .catch(() => {
        if (vivo) setExistentes([]);
      });
    return () => {
      vivo = false;
    };
  }, [abierto, projectId, fileId, formato]);

  // Mientras el servidor lee la revisión nueva, se pregunta cada pocos segundos (aplicar la necesita lista).
  const pendiente = revision != null && ABIERTAS.has(revision.estado) && revision.planoNuevoStatus === 'pending';
  useEffect(() => {
    if (!abierto || !pendiente) return;
    const t = setInterval(() => {
      listarRevisionesPlano(projectId, fileId)
        .then((r) => {
          const x = r.revisiones.find((y) => y.id === revision?.id);
          if (x) setRevision((prev) => (prev && prev.id === x.id ? { ...prev, planoNuevoStatus: x.planoNuevoStatus } : prev));
        })
        .catch(() => undefined);
    }, 4000);
    return () => clearInterval(t);
  }, [abierto, pendiente, projectId, fileId, revision?.id]);

  // Cerrar cancela la medición en curso (se termina su worker y se libera la memoria).
  useEffect(() => {
    if (!abierto) cancelar.current?.abort();
  }, [abierto]);
  useEffect(() => () => cancelar.current?.abort(), []);

  /** Mide las dos revisiones (una detrás de otra), compara con la hoja y guarda la comparación. */
  const comparar = useCallback(
    async (rev: PlanoRevisionResumen) => {
      const ctrl = new AbortController();
      cancelar.current?.abort();
      cancelar.current = ctrl;
      setError(null);
      setDesactualizada(false);
      setComparacion(null);
      try {
        const [pc, hoja] = await Promise.all([getParametrosCambioDePlano(), listarRevisionesPlano(projectId, fileId)]);
        let comp: ComparacionDePlanos;
        if (formato === 'dwg') {
          // DWG/DXF: el servidor lee las dos revisiones; aquí solo se espera a la nueva y se compara con sus inventarios.
          setMidiendo({ cual: 2, avance: null, servidor: true });
          const leidos = await leerPlanosDeLaRevisionDwg(projectId, rev.fileAnteriorId, rev.fileNuevoId, { signal: ctrl.signal });
          const res = compararRevisionDwg({
            a: leidos.a,
            b: leidos.b,
            altosVanos: leidos.altosVanos,
            renglones: hoja.renglones,
            parametros: pc,
            fechaRevision: fechaDeRevision(rev.createdAt),
          });
          if ('frenada' in res) {
            // No se puede comparar (otra unidad, plano sin leer…): se dice por qué y no se guarda nada.
            setError(res.frenada);
            return;
          }
          comp = res.comparacion;
          setAvisosDwg(res.avisos);
          setCambiosVista(res.cambiosDeVistas);
        } else {
          const parametros = await getParametrosMedicionIfc();
          setMidiendo({ cual: 1, avance: null });
          const a = await medirIfcSinVisor({ projectId, fileId: rev.fileAnteriorId, parametros, signal: ctrl.signal, onAvance: (avance) => setMidiendo({ cual: 1, avance }) });
          setMidiendo({ cual: 2, avance: null });
          const b = await medirIfcSinVisor({ projectId, fileId: rev.fileNuevoId, parametros, signal: ctrl.signal, onAvance: (avance) => setMidiendo({ cual: 2, avance }) });
          comp = compararRevisionIfc({ a, b, renglones: hoja.renglones, parametros: pc, fechaRevision: fechaDeRevision(rev.createdAt) });
        }
        // Si se cerró el diálogo (o se lanzó otra comparación) mientras se medía, no se guarda una comparación que nadie pidió.
        if (ctrl.signal.aborted) return;
        const guardada = await guardarComparacionPlano(projectId, fileId, rev.id, comp, hoja.huellaHoja);
        setRenglones(hoja.renglones);
        setComparacion(comp);
        setRevision(guardada);
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        if (e instanceof ApiClientError && e.code === 'REVISION_DESACTUALIZADA') setDesactualizada(true);
        setError(mensajeDe(e, 'No se pudo comparar la revisión nueva.'));
      } finally {
        if (cancelar.current === ctrl) {
          cancelar.current = null;
          setMidiendo(null);
        }
      }
    },
    [projectId, fileId, formato],
  );

  /** Sube el archivo nuevo al proyecto, crea la revisión y compara. */
  const subirRevision = async (archivo: File) => {
    setError(null);
    if (!esDelFormato(archivo.name, formato)) {
      setError(formato === 'ifc' ? 'La revisión tiene que ser un modelo IFC, como el plano de tu hoja.' : 'La revisión tiene que ser un dibujo DWG o DXF, como el plano de tu hoja.');
      return;
    }
    setSubida(0);
    try {
      const r = await uploadFiles(projectId, [archivo], setSubida);
      const id = r.files[0]?.id ?? r.deduplicated[0]?.duplicateOf;
      if (!id) throw new Error('No se pudo subir el archivo.');
      const rev = await crearRevisionPlano(projectId, fileId, id);
      setRevision(rev);
      setSubida(null);
      void comparar(rev);
    } catch (e) {
      setSubida(null);
      if (e instanceof ApiClientError && e.code === 'REVISION_ABIERTA') {
        await cargar();
      }
      setError(mensajeDe(e, 'No se pudo subir la revisión.'));
    }
  };

  /** Usa un plano que ya está en el proyecto como revisión (sin subirlo otra vez) y compara. */
  const usarExistente = async (archivoId: string) => {
    setError(null);
    setSubida(0);
    try {
      const rev = await crearRevisionPlano(projectId, fileId, archivoId);
      setRevision(rev);
      setSubida(null);
      void comparar(rev);
    } catch (e) {
      setSubida(null);
      if (e instanceof ApiClientError && e.code === 'REVISION_ABIERTA') await cargar();
      setError(mensajeDe(e, 'No se pudo usar ese plano como revisión.'));
    }
  };

  const hacer = async (nombre: string, fn: () => Promise<void>) => {
    if (trabajando) return;
    setTrabajando(nombre);
    setError(null);
    try {
      await fn();
    } catch (e) {
      if (e instanceof ApiClientError && (e.code === 'REVISION_DESACTUALIZADA' || e.code === 'SIN_COMPARAR')) setDesactualizada(true);
      setError(mensajeDe(e, 'No se pudo completar. Tu hoja no cambió.'));
    } finally {
      setTrabajando(null);
      setConfirmar(null);
    }
  };

  const aplicar = () =>
    hacer('aplicar', async () => {
      if (!revision) return;
      const r = await aplicarRevisionPlano(projectId, fileId, revision.id);
      const partes = [
        r.cantidades > 0 ? `${r.cantidades} ${r.cantidades === 1 ? 'cantidad cambió' : 'cantidades cambiaron'}` : null,
        r.paraRevisar > 0 ? `${r.paraRevisar} ${r.paraRevisar === 1 ? 'concepto queda' : 'conceptos quedan'} para revisar` : null,
      ].filter(Boolean);
      avisar(`Revisión aplicada${partes.length ? `: ${partes.join(' y ')}` : ''}.`);
      setRevision(r.revision);
      setRenglones(r.renglones);
      onHojaCambiada(r.revision.fileNuevoId);
    });

  const revertir = () =>
    hacer('revertir', async () => {
      if (!revision) return;
      const r = await revertirRevisionPlano(projectId, fileId, revision.id);
      avisar('Tu hoja volvió a como estaba.');
      setRevision(null);
      setComparacion(null);
      setRenglones(r.renglones);
      onHojaCambiada(r.revision.fileAnteriorId);
    });

  const descartar = () =>
    hacer('descartar', async () => {
      if (!revision) return;
      cancelar.current?.abort();
      await descartarRevisionPlano(projectId, fileId, revision.id);
      avisar('Revisión descartada. Tu hoja no cambió.');
      setRevision(null);
      setComparacion(null);
      setDesactualizada(false);
    });

  /** Prueba con OTRA revisión ya subida: se descarta la comparación actual (la hoja no cambia) y se compara con la elegida. */
  const cambiarDeRevision = async (archivoId: string) => {
    if (!revision || trabajando || midiendo || subida != null) return;
    setError(null);
    try {
      cancelar.current?.abort();
      await descartarRevisionPlano(projectId, fileId, revision.id);
      setRevision(null);
      setComparacion(null);
      setFoco(null);
      setPestana('presupuesto');
      setDesactualizada(false);
    } catch (e) {
      setError(mensajeDe(e, 'No se pudo cambiar de revisión. Tu hoja no cambió.'));
      return;
    }
    await usarExistente(archivoId);
  };

  const usarPropuesta = (renglonId: string) =>
    hacer(`usar:${renglonId}`, async () => {
      if (!revision) return;
      const r = await usarPropuestaRevision(projectId, fileId, revision.id, renglonId);
      setRenglones((rs) => rs.map((x) => (x.id === r.renglon.id ? r.renglon : x)));
      setRevision((prev) => (prev ? { ...prev, puedeRevertir: r.puedeRevertir } : prev));
      avisar('Se usó la cantidad del plano nuevo.');
      onHojaCambiada(null);
    });

  const porId = useMemo(() => new Map(renglones.map((r) => [r.id, r] as const)), [renglones]);
  const resumen = useMemo(() => (comparacion ? resumenDeAplicar(comparacion) : null), [comparacion]);
  const filas = useMemo(() => (comparacion ? ordenarImpacto(comparacion.renglones) : []), [comparacion]);
  const sinCambio = filas.filter((x) => x.estado === 'igual' || x.estado === 'no_se_recalcula');
  const conCambio = filas.filter((x) => x.estado !== 'igual' && x.estado !== 'no_se_recalcula');
  const paraRevisar = filas.filter((x) => x.estado === 'revisar');
  const aplicada = revision?.estado === 'aplicada';
  const abiertaRev = revision != null && ABIERTAS.has(revision.estado);
  const ocupado = midiendo != null || subida != null || trabajando != null;

  const nombreRenglon = (id: string) => {
    const r = porId.get(id);
    return r ? `${r.resourceClave ? `${r.resourceClave} · ` : ''}${r.conceptoDescripcion}` : 'Concepto';
  };

  const cifras = comparacion && (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
      <Cifra marca="Antes" valor={pesos(comparacion.totalAntes)} />
      <Cifra marca="Ahora" valor={pesos(comparacion.totalNuevo)} colorMarca="var(--accent)" />
      <Cifra
        marca="Diferencia"
        valor={`${comparacion.totalNuevo - comparacion.totalAntes > 0 ? '+' : ''}${pesos(comparacion.totalNuevo - comparacion.totalAntes)}`}
        sub={
          comparacion.totalAntes > 0
            ? `${((comparacion.totalNuevo - comparacion.totalAntes) / comparacion.totalAntes * 100).toLocaleString('es-MX', { maximumFractionDigits: 1, signDisplay: 'exceptZero' })} %`
            : undefined
        }
        color={comparacion.totalNuevo < comparacion.totalAntes ? 'var(--ok)' : comparacion.totalNuevo > comparacion.totalAntes ? 'var(--bad)' : undefined}
      />
    </div>
  );

  const filaRenglon = (x: ImpactoDeRenglon) => {
    const r = porId.get(x.id);
    const cambia = x.estado === 'cambia' || x.estado === 'ya_no_esta' || x.estado === 'revisar';
    const antes = importeDeHoja(x.importeAntes, x.apuIaImporteAntes);
    const despues = importeDespues(x);
    const nueva = x.cantidadNueva;
    const pas = PASTILLA_RENGLON[x.estado];
    // DWG: tocar un concepto lo enfoca en el dibujo (el resto se atenúa), como en el modelo IFC.
    const elementos = formato === 'dwg' && r?.seleccion ? elementosDeSeleccion(r.seleccion) : [];
    const verEnDibujo = () => {
      const capas = elementos.filter((e) => e.tipo === 'capa').map((e) => e.clave);
      const bloques = elementos.filter((e) => e.tipo === 'bloque').map((e) => e.clave);
      if (capas.length + bloques.length === 0) return;
      setFoco({ n: ++contadorVista.current, titulo: nombreRenglon(x.id), capas, bloques });
      setPestana('modelo');
    };
    // La razón de cada fila en ámbar: qué pasó con su cantidad (el «Revisar» ya trae la suya).
    const razon =
      x.estado === 'cambia'
        ? x.motivo ?? `La cantidad pasó de ${fmtCant(x.cantidadAntes)} a ${nueva != null ? fmtCant(nueva) : '—'} ${unidadBonita(r?.unidad)} porque cambió lo que mide este concepto en el plano.`
        : x.estado === 'ya_no_esta'
          ? x.motivo ?? 'Lo que medía este concepto ya no está en la revisión nueva.'
          : null;
    return (
      <tr
        key={x.id}
        style={{ background: cambia ? 'var(--warn-weak)' : undefined, cursor: elementos.length > 0 ? 'pointer' : undefined }}
        title={elementos.length > 0 ? 'Toca para verlo en el dibujo' : undefined}
        onClick={elementos.length > 0 ? verEnDibujo : undefined}
      >
        <td style={estilos.td}>
          <div style={{ color: 'var(--text-primary)' }} title={r?.conceptoDescripcion}>
            {corto(nombreRenglon(x.id), 90)}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
            <Pastilla texto={pas.texto} tono={pas.tono} />
            {x.piezas.cambioDeTipo > 0 && <Pastilla texto="Cambió de tipo" tono="accent" />}
            {x.piezas.dudosas > 0 && <Pastilla texto={NO_ES_SEGURO} tono="warn" />}
          </div>
          {razon && <div style={{ ...estilos.sub, color: 'var(--text-secondary)' }}>{razon}</div>}
          {x.piezas.dudosas > 0 && (
            <div style={{ ...estilos.sub, color: 'var(--text-secondary)' }}>
              No se pudo asegurar que lo de antes y lo de ahora sean lo mismo (se emparejaron por su forma, no por su nombre): revisa este concepto.
            </div>
          )}
          {x.aMano && nueva != null && x.estado === 'revisar' && (
            <div style={{ ...estilos.sub, color: 'var(--text-secondary)' }}>
              Corregiste esta cantidad a mano. El plano nuevo mide {fmtCant(nueva)} {unidadBonita(r?.unidad)}.
            </div>
          )}
          {/* La razón de un «Revisar» va en su propia fila (antes se repetía en una lista aparte). */}
          {x.estado === 'revisar' && x.motivo && !(x.aMano && nueva != null) && (
            <div style={{ ...estilos.sub, color: 'var(--text-secondary)' }}>{x.motivo}</div>
          )}
        </td>
        <td style={{ ...estilos.td, ...estilos.num }}>
          {fmtCant(x.cantidadAntes)} {unidadBonita(r?.unidad)}
        </td>
        <td style={{ ...estilos.td, ...estilos.num }}>
          {nueva != null ? (
            <>
              {fmtCant(nueva)} {unidadBonita(r?.unidad)}
              {x.estado === 'revisar' && <div style={estilos.sub}>propuesta</div>}
            </>
          ) : (
            '—'
          )}
        </td>
        <td style={{ ...estilos.td, ...estilos.num, color: x.delta != null && x.delta !== 0 ? (x.delta > 0 ? 'var(--bad)' : 'var(--ok)') : undefined }}>
          {x.delta != null && x.delta !== 0 ? `${x.delta > 0 ? '+' : ''}${fmtCant(x.delta)}` : '—'}
        </td>
        <td style={{ ...estilos.td, ...estilos.num }}>
          {despues !== antes ? (
            <>
              <span style={{ color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>{pesos(antes)}</span>
              <div style={{ fontWeight: 600 }}>{pesos(despues)}</div>
            </>
          ) : (
            pesos(antes)
          )}
        </td>
      </tr>
    );
  };

  const tabla = (xs: ImpactoDeRenglon[]) => (
    // flexShrink 0: dentro del diálogo (columna flexible) la tabla se encogía a una sola fila cuando las secciones de abajo
    // crecían; el diálogo entero es el que se desplaza, no esta caja.
    <div style={{ maxHeight: '38vh', minHeight: 'min(220px, 38vh)', flexShrink: 0, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr>
            <th style={estilos.th}>Concepto</th>
            <th style={{ ...estilos.th, ...estilos.num }}>Antes</th>
            <th style={{ ...estilos.th, ...estilos.num }}>Ahora</th>
            <th style={{ ...estilos.th, ...estilos.num }}>Diferencia</th>
            <th style={{ ...estilos.th, ...estilos.num }}>Importe</th>
          </tr>
        </thead>
        <tbody>{xs.map(filaRenglon)}</tbody>
      </table>
    </div>
  );

  // Renglones que, ya aplicada la revisión, tienen una cantidad nueva para usar.
  const conPropuesta = aplicada ? renglones.filter((r) => r.propuestaRevision && r.propuestaRevision.revisionId === revision?.id) : [];
  const impactoDe = useMemo(() => new Map((comparacion?.renglones ?? []).map((x) => [x.id, x] as const)), [comparacion]);

  if (!abierto) return null;
  return (
    <>
      <Dialog open onOpenChange={(_, d) => !d.open && !trabajando && subida == null && confirmar == null && onCerrar()}>
        <DialogSurface style={{ width: 1100, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 32px)' }}>
          <DialogBody>
            <DialogTitle>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Cambió el plano <FieldHelp helpKey="planos.cambio_de_plano" variante="accion" />
              </span>
              {revision && (
                <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Qué plano usa la hoja HOY: el original, o la revisión si ya se aplicó. */}
                  <span>
                    Tu hoja usa hoy: <b style={{ color: 'var(--text-primary)' }}>«{(aplicada ? revision.fileNuevoNombre : revision.fileAnteriorNombre) ?? 'el plano de tu hoja'}»</b>{' '}
                    {aplicada ? '(la revisión que ya aplicaste)' : '(el plano original)'}
                  </span>
                  <span>
                    {aplicada ? 'Plano anterior' : 'Revisión nueva (todavía no se aplica)'}: «{(aplicada ? revision.fileAnteriorNombre : revision.fileNuevoNombre) ?? 'el otro plano'}»
                  </span>
                </div>
              )}
            </DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {error && <div style={estilos.aviso}>{error}</div>}
              {cargando && <Spinner size="small" label="Leyendo las revisiones de este plano…" />}

              {/* 1. Subir la revisión nueva */}
              {!cargando && !abiertaRev && subida == null && (
                <div style={estilos.guia}>
                  {aplicada
                    ? '¿Llegó otra revisión? Súbela y te digo cuánto cambia tu hoja.'
                    : (formato === 'ifc'
                      ? 'Sube la revisión nueva del modelo (otro IFC del mismo proyecto). Se compara con el modelo con el que armaste esta hoja y te digo, concepto por concepto, cuánto cambia tu presupuesto. Nada cambia en tu hoja hasta que lo apliques.'
                      : 'Sube la revisión nueva del dibujo (otro DWG o DXF del mismo proyecto). Se compara con el dibujo con el que armaste esta hoja y te digo, concepto por concepto, cuánto cambia tu presupuesto. Nada cambia en tu hoja hasta que lo apliques.')}
                  <div style={{ marginTop: 8 }}>
                    <input
                      ref={entrada}
                      type="file"
                      accept={formato === 'ifc' ? '.ifc' : '.dwg,.dxf'}
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) void subirRevision(f);
                      }}
                    />
                    <Button appearance={aplicada ? 'secondary' : 'primary'} icon={<ArrowUpload16Regular />} disabled={ocupado} onClick={() => entrada.current?.click()}>
                      {formato === 'ifc' ? 'Elegir el modelo nuevo' : 'Elegir el dibujo nuevo'}
                    </Button>
                  </div>
                  {existentes.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={estilos.sub}>O usa uno que ya subiste a este proyecto:</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                        <select
                          value={elegidoExistente}
                          onChange={(e) => setElegidoExistente(e.target.value)}
                          disabled={ocupado}
                          style={{ minWidth: 260, maxWidth: '100%', padding: '5px 8px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13 }}
                        >
                          <option value="">Elegir un plano…</option>
                          {existentes.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.nombre}
                            </option>
                          ))}
                        </select>
                        <Button size="small" appearance="primary" disabled={ocupado || !elegidoExistente} onClick={() => void usarExistente(elegidoExistente)}>
                          Comparar con este
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {subida != null && (
                <div>
                  <div style={estilos.sub}>Subiendo la revisión nueva…</div>
                  <ProgressBar value={subida} max={1} />
                </div>
              )}

              {/* 2. Medir y comparar */}
              {midiendo?.servidor && (
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                    <Spinner size="extra-tiny" style={{ display: 'inline-flex', verticalAlign: -2, marginRight: 6 }} />
                    Leyendo la revisión nueva en el servidor…
                  </div>
                  <div style={estilos.sub}>En cuanto termine se compara con tu hoja. Un dibujo grande puede tardar unos minutos.</div>
                </div>
              )}
              {midiendo && !midiendo.servidor && (
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                    {midiendo.cual === 1 ? 'Midiendo el plano con el que armaste tu hoja (1 de 2)' : 'Midiendo la revisión nueva (2 de 2)'} ·{' '}
                    {textoDeAvanceDeMedir(midiendo.avance)}
                  </div>
                  <ProgressBar value={midiendo.avance?.fraccion ?? undefined} max={1} />
                  <div style={estilos.sub}>Se mide en tu navegador, una revisión detrás de la otra. Un modelo grande puede tardar unos minutos.</div>
                </div>
              )}
              {abiertaRev && !midiendo && !comparacion && !cargando && (
                <div style={estilos.guia}>
                  {revision.estado === 'subida' ? 'La revisión nueva ya está en el proyecto. Compárala con tu hoja.' : 'Compara de nuevo la revisión con tu hoja.'}
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <Button appearance="primary" disabled={ocupado} onClick={() => void comparar(revision)}>
                      Comparar con mi hoja
                    </Button>
                    <Button appearance="subtle" disabled={ocupado} onClick={() => setConfirmar('descartar')}>
                      Descartar revisión
                    </Button>
                  </div>
                </div>
              )}
              {desactualizada && abiertaRev && comparacion && !midiendo && (
                <div>
                  <Button appearance="primary" disabled={ocupado} onClick={() => void comparar(revision)}>
                    Volver a comparar
                  </Button>
                </div>
              )}

              {/* 3. El resultado */}
              {comparacion && !midiendo && (
                <>
                  {comparacion.identidadesRegeneradas && (
                    <div style={estilos.aviso}>
                      Las piezas de este modelo vienen con otros nombres internos: las emparejamos por su lugar y su medida. Revisa las marcadas.
                    </div>
                  )}
                  {formato === 'dwg' && avisosDwg.map((a) => (
                    <div key={a} style={estilos.aviso}>
                      {a}
                    </div>
                  ))}
                  {parecePocoOtro && !seguirAunqueSeaOtro && !aplicada && (
                    <div style={{ ...estilos.aviso, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{formato === 'ifc' ? 'Este modelo parece ser otro' : 'Este dibujo parece ser otro plano'}</div>
                      <div>
                        {formato === 'ifc'
                          ? 'Casi nada de lo que trae coincide con el modelo con el que armaste tu hoja. Puede ser otro modelo y no una revisión del mismo, o estar movido o a otra escala.'
                          : 'Casi nada de lo que dibuja coincide con el plano con el que armaste tu hoja. Puede ser otro plano y no una revisión del mismo, o estar movido o a otra escala.'}
                      </div>
                      <div>
                        <b>Te recomendamos subirlo en un proyecto nuevo</b> y armar ahí su presupuesto. Si lo comparas aquí, tu hoja ya está generada con
                        el plano original y los resultados pueden salir mal.
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button appearance="primary" disabled={ocupado} onClick={() => descartar()}>
                          No continuar
                        </Button>
                        <Button disabled={ocupado} onClick={() => setSeguirAunqueSeaOtro(true)}>
                          Continuar de todos modos
                        </Button>
                      </div>
                    </div>
                  )}
                  {parecePocoOtro && seguirAunqueSeaOtro && !aplicada && (
                    <div style={estilos.aviso}>
                      <b>Ojo:</b> este {cosa} parece {formato === 'ifc' ? 'otro' : 'otro plano'} y tu hoja ya está armada con el original. Los resultados pueden ser malos; lo más seguro es hacerlo
                      en un proyecto nuevo.
                    </div>
                  )}
                  {cifras}
                  {formato === 'dwg' && cambiosVista.length > 0 && (
                    <Seccion titulo="Cambios en las plantas">
                      <ul style={estilos.lista}>
                        {cambiosVista.map((v, i) => (
                          <li key={i}>
                            <b>{v.rotulo}</b>: {v.detalle}
                          </li>
                        ))}
                      </ul>
                    </Seccion>
                  )}
                  {conPropuesta.length > 0 && (
                    <Seccion titulo="Cantidades nuevas para revisar">
                      <ul style={estilos.lista}>
                        {conPropuesta.map((r) => {
                          const y = r.propuestaRevision!.cantidad;
                          const aMano = impactoDe.get(r.id)?.aMano;
                          return (
                            <li key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span>
                                <b>{corto(nombreRenglon(r.id), 70)}</b>:{' '}
                                {aMano
                                  ? `Corregiste esta cantidad a mano. El plano nuevo mide ${fmtCant(y)} ${unidadBonita(r.unidad)}.`
                                  : `Con la revisión nueva el plano mide ${fmtCant(y)} ${unidadBonita(r.unidad)} (hoy tiene ${fmtCant(r.cantidad)}).`}
                              </span>
                              <Button size="small" disabled={ocupado} onClick={() => void usarPropuesta(r.id)}>
                                Usar {fmtCant(y)}
                              </Button>
                            </li>
                          );
                        })}
                      </ul>
                    </Seccion>
                  )}

                  {/* Probar con otra de las revisiones ya subidas (se comparan de una en una contra la hoja). */}
                  {abiertaRev && !aplicada && !midiendo && existentes.some((f) => f.id !== revision?.fileNuevoId) && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={estilos.sub}>¿Otra revisión?</span>
                      <select
                        value={elegidoExistente}
                        onChange={(e) => setElegidoExistente(e.target.value)}
                        disabled={ocupado}
                        style={{ minWidth: 240, maxWidth: '100%', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 12.5 }}
                      >
                        <option value="">Elegir otro plano…</option>
                        {existentes
                          .filter((f) => f.id !== revision?.fileNuevoId)
                          .map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.nombre}
                            </option>
                          ))}
                      </select>
                      <Button size="small" disabled={ocupado || !elegidoExistente || elegidoExistente === revision?.fileNuevoId} onClick={() => void cambiarDeRevision(elegidoExistente)}>
                        Comparar con esta otra
                      </Button>
                    </div>
                  )}
                  {/* Dos preguntas distintas, dos pestañas: «¿cuánto cambia mi presupuesto?» y «¿qué cambió en el modelo?» (con el 3D). */}
                  <TabList selectedValue={pestana} onTabSelect={(_, d) => setPestana(d.value as 'presupuesto' | 'modelo')} size="small">
                    <Tab value="presupuesto">
                      Tu presupuesto ({conCambio.length} {conCambio.length === 1 ? 'concepto cambia' : 'conceptos cambian'})
                    </Tab>
                    {comparacion.piezas.length > 0 && (
                      <Tab value="modelo">
                        Qué cambió en {formato === 'ifc' ? 'el modelo' : 'el dibujo'} ({comparacion.piezas.length} {comparacion.piezas.length === 1 ? (formato === 'ifc' ? 'pieza' : 'elemento') : formato === 'ifc' ? 'piezas' : 'elementos'})
                      </Tab>
                    )}
                  </TabList>
                  {pestana === 'presupuesto' && (
                    <>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    «Ahora» suma lo que se aplica solo; lo que queda para revisar cuenta con su importe de hoy. Las filas en ámbar son las que
                    cambian o hay que revisar, y cada una dice por qué.
                    {formato === 'dwg' ? ' Toca un concepto para verlo en el dibujo.' : ''}
                  </div>
                  {conCambio.length > 0 ? (
                    tabla(conCambio)
                  ) : (
                    <div style={estilos.guia}>La revisión nueva no cambia ninguna cantidad de tu hoja.</div>
                  )}
                  {sinCambio.length > 0 && (
                    <details>
                      <summary style={{ cursor: 'pointer', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        {sinCambio.length} {sinCambio.length === 1 ? 'concepto no cambia' : 'conceptos no cambian'}
                      </summary>
                      <div style={{ marginTop: 6 }}>{tabla(sinCambio)}</div>
                    </details>
                  )}

                  {paraRevisar.length > 0 && !aplicada && (
                    <div style={estilos.sub}>
                      Los renglones «Revisar» quedan marcados al aplicar y su cantidad no cambia; la del plano nuevo queda como propuesta.
                    </div>
                  )}

                  {comparacion.nuevosSinConcepto.length > 0 && (
                    <Seccion titulo="Piezas nuevas sin concepto">
                      <div style={estilos.sub}>Están en la revisión nueva y ningún concepto de tu hoja las cubre. No se agregan solas: asígnales concepto en el plano.</div>
                      <ul style={estilos.lista}>
                        {comparacion.nuevosSinConcepto.map((n) => (
                          <li key={`${n.origen}:${n.grupo}`}>
                            <b>{n.grupo}</b>: {n.piezas.length} {n.piezas.length === 1 ? 'pieza' : 'piezas'}
                            {n.origen === 'cambio_de_tipo' ? ' que antes eran de otro tipo' : ' nuevas'}
                          </li>
                        ))}
                      </ul>
                    </Seccion>
                  )}

                    </>
                  )}
                  {pestana === 'modelo' && comparacion.piezas.length > 0 && revision && (
                    <QueCambioEnElModelo
                      formato={formato}
                      projectId={projectId}
                      fileAnteriorId={revision.fileAnteriorId}
                      fileNuevoId={revision.fileNuevoId}
                      piezas={comparacion.piezas}
                      foco={foco}
                      nombreAnterior={revision.fileAnteriorNombre ?? 'el plano de tu hoja'}
                      nombreNuevo={revision.fileNuevoNombre ?? 'la revisión nueva'}
                      onQuitarFoco={() => setFoco(null)}
                      diffDwg={diffDwg}
                      calculandoDiff={calculandoDiff}
                      bytesDwg={bytesDwg}
                      centrosDwg={centrosDwg}
                    />
                  )}
                  {abiertaRev && revision.planoNuevoStatus === 'pending' && (
                    <div style={estilos.sub}>
                      <Spinner size="extra-tiny" style={{ display: 'inline-flex', verticalAlign: -2, marginRight: 6 }} />
                      La revisión nueva todavía se está terminando de leer en el servidor; podrás aplicarla en cuanto termine.
                    </div>
                  )}
                  {abiertaRev && revision.planoNuevoStatus === 'failed' && (
                    <div style={estilos.aviso}>No se pudo leer la revisión nueva en el servidor: descártala y vuelve a subirla.</div>
                  )}
                </>
              )}
            </DialogContent>
            {/* Una sola fila: lo destructivo a la izquierda; a la derecha lo que se hace con la revisión y «Cerrar». */}
            <DialogActions
              style={{ display: 'flex', flexWrap: 'nowrap', width: '100%', gridColumn: '1 / -1', whiteSpace: 'nowrap', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {abiertaRev && comparacion && !midiendo && (
                  <Button appearance="subtle" disabled={ocupado} onClick={() => setConfirmar('descartar')}>
                    Descartar revisión
                  </Button>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 8 }}>
                {abiertaRev && comparacion && !midiendo && (
                  <>
                    <Button appearance="secondary" disabled={ocupado} onClick={() => void comparar(revision)}>
                      Volver a comparar
                    </Button>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Button
                        appearance="primary"
                        disabled={ocupado || desactualizada || revision.planoNuevoStatus !== 'ready' || (parecePocoOtro && !seguirAunqueSeaOtro)}
                        onClick={() => setConfirmar('aplicar')}
                      >
                        Aplicar a mi hoja
                      </Button>
                      <FieldHelp helpKey="planos.cambio_aplicar" variante="accion" />
                    </span>
                  </>
                )}
                {aplicada && revision.puedeRevertir && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Button appearance="secondary" disabled={ocupado} onClick={() => setConfirmar('revertir')}>
                      Volver a como estaba
                    </Button>
                    <FieldHelp helpKey="planos.cambio_volver" variante="accion" />
                  </span>
                )}
                <Button appearance="secondary" disabled={trabajando != null || subida != null} onClick={onCerrar}>
                  Cerrar
                </Button>
              </div>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={confirmar != null} onOpenChange={(_, d) => !d.open && !trabajando && setConfirmar(null)}>
        <DialogSurface style={{ maxWidth: 520 }}>
          <DialogBody>
            <DialogTitle>
              {confirmar === 'aplicar' ? 'Aplicar la revisión a tu hoja' : confirmar === 'revertir' ? 'Volver a como estaba' : 'Descartar la revisión'}
            </DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13.5 }}>
              {confirmar === 'aplicar' && parecePocoOtro && (
                <div style={estilos.aviso}>
                  <b>Ojo:</b> este {cosa} parece {formato === 'ifc' ? 'otro' : 'otro plano'} y tu hoja ya está generada con el original: los resultados pueden ser malos. Lo más seguro es
                  hacerlo en un proyecto nuevo. Puedes volver a como estaba después, pero revisa bien antes de seguir.
                </div>
              )}
              {confirmar === 'aplicar' && resumen && comparacion && (
                <>
                  <div>Tu hoja va a cambiar así:</div>
                  <ul style={estilos.lista}>
                    {resumen.cantidades > 0 && <li>{resumen.cantidades === 1 ? '1 concepto cambia' : `${resumen.cantidades} conceptos cambian`} de cantidad.</li>}
                    {resumen.yaNoEstan > 0 && (
                      <li>{resumen.yaNoEstan === 1 ? '1 concepto queda' : `${resumen.yaNoEstan} conceptos quedan`} en 0 porque ya no está en el plano nuevo (no se borra).</li>
                    )}
                    {resumen.selecciones > 0 && (
                      <li>{resumen.selecciones === 1 ? '1 concepto pasa' : `${resumen.selecciones} conceptos pasan`} a medir las piezas del plano nuevo.</li>
                    )}
                    {resumen.revisar > 0 && (
                      <li>
                        {resumen.revisar === 1 ? '1 concepto queda marcado' : `${resumen.revisar} conceptos quedan marcados`} para revisar: su cantidad no cambia.
                      </li>
                    )}
                    <li>
                      El total pasa de {pesos(comparacion.totalAntes)} a {pesos(comparacion.totalNuevo)}.
                    </li>
                    <li>Tu hoja pasa a usar «{revision?.fileNuevoNombre ?? 'la revisión nueva'}».</li>
                  </ul>
                  <div style={estilos.sub}>
                    Si algo no te convence, «Volver a como estaba» la deja exactamente como está ahora, mientras no cambies la hoja después.
                  </div>
                </>
              )}
              {confirmar === 'revertir' && (
                <div>
                  Tu hoja vuelve a quedar exactamente como estaba antes de aplicar la revisión, con el plano «{revision?.fileAnteriorNombre ?? 'anterior'}». La
                  revisión nueva sigue en el proyecto.
                </div>
              )}
              {confirmar === 'descartar' && <div>Se descarta la comparación con «{revision?.fileNuevoNombre ?? 'la revisión nueva'}». Tu hoja no cambia.</div>}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" disabled={trabajando != null} onClick={() => setConfirmar(null)}>
                Cancelar
              </Button>
              <Button
                appearance="primary"
                disabled={trabajando != null}
                onClick={() => void (confirmar === 'aplicar' ? aplicar() : confirmar === 'revertir' ? revertir() : descartar())}
              >
                {trabajando ? <Spinner size="tiny" /> : confirmar === 'aplicar' ? 'Sí, aplicar' : confirmar === 'revertir' ? 'Sí, volver' : 'Descartar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

/**
 * Pastilla en la barra de la hoja: «Revisión aplicada · Volver a como estaba». Solo aparece si la última revisión
 * aplicada a esta hoja todavía se puede revertir. Al pulsarla se abre «Cambió el plano».
 */
export function PastillaRevisionAplicada({
  projectId,
  fileId,
  refrescar,
  onAbrir,
}: {
  projectId: string;
  fileId: string;
  /** Cambia cuando cambia la hoja (para volver a preguntar). */
  refrescar: unknown;
  onAbrir: () => void;
}): JSX.Element | null {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    let vivo = true;
    listarRevisionesPlano(projectId, fileId)
      .then((r) => {
        if (vivo) setVisible(r.revisiones.some((x) => x.estado === 'aplicada' && x.puedeRevertir && x.fileNuevoId === fileId));
      })
      .catch(() => {
        if (vivo) setVisible(false);
      });
    return () => {
      vivo = false;
    };
  }, [projectId, fileId, refrescar]);
  if (!visible) return null;
  return (
    <Button size="small" appearance="subtle" onClick={onAbrir} title="La hoja usa la revisión nueva del plano. Puedes volver a como estaba.">
      <Pastilla texto="Revisión aplicada" tono="accent" />
      <span style={{ marginLeft: 6 }}>Volver a como estaba</span>
    </Button>
  );
}

const LEYENDA_TONO: Record<'ok' | 'bad' | 'warn' | 'accent', string> = {
  ok: 'Nuevo',
  bad: 'Ya no está',
  warn: 'Cambió de medida',
  accent: 'Cambió de tipo o se movió',
};

/**
 * Si lo que cambió de un concepto solo existe en un lado (una puerta quitada solo está en «Antes»; un muro nuevo, solo en
 * «Ahora»), cuál lado mostrar. El nombre del inventario puede venir sin el prefijo de xref: se compara por el final.
 */
function ladoDelConcepto(piezas: readonly CambioDePieza[], foco: { capas: string[]; bloques: string[] } | null): 'a' | 'b' | null {
  if (!foco) return null;
  const pareja = piezas.find((p) => {
    const g = (p.a ?? p.b)?.grupo;
    return !!g && (foco.capas.includes(g) || foco.bloques.some((b) => b === g || b.split('$').pop() === g));
  });
  return pareja?.tipo === 'quitada' ? 'a' : pareja?.tipo === 'agregada' ? 'b' : null;
}

/**
 * Qué cambió en el modelo: la lista de piezas y, al lado, UN visor 3D que alterna entre «Antes» (el modelo con el que
 * armaste la hoja) y «Ahora» (la revisión nueva). Clic en una fila enfoca esa pieza; clic en una pieza del visor
 * resalta su fila. El visor se monta solo cuando ya no se está midiendo (nunca dos modelos a la vez) y se remonta al
 * alternar, para soltar el modelo anterior antes de cargar el otro.
 */
function QueCambioEnElModelo({
  formato,
  projectId,
  fileAnteriorId,
  fileNuevoId,
  piezas,
  foco,
  nombreAnterior,
  nombreNuevo,
  onQuitarFoco,
  diffDwg,
  calculandoDiff,
  bytesDwg,
  centrosDwg,
}: {
  /** DWG/DXF: el visor del dibujo con lo que cambió resaltado (un solo color) y, al elegir un concepto, solo lo suyo. */
  formato: 'ifc' | 'dwg';
  projectId: string;
  fileAnteriorId: string;
  fileNuevoId: string;
  piezas: CambioDePieza[];
  /** DWG: UN concepto elegido en la tabla de presupuesto: solo sus capas y bloques van resaltados, el resto se atenúa. */
  foco: { n: number; titulo: string; capas: string[]; bloques: string[] } | null;
  /** Nombres de los dos archivos (para decir cuál se está viendo). */
  nombreAnterior: string;
  nombreNuevo: string;
  onQuitarFoco: () => void;
  /** DWG: el diff entidad por entidad que calcula el diálogo (null = no se pudo / no aplica). */
  diffDwg: ResultadoDiffEntidades | null;
  calculandoDiff: boolean;
  /** Los bytes de los dos DXF ya bajados (el visor no los baja otra vez). */
  bytesDwg: { a: ArrayBuffer; b: ArrayBuffer } | null;
  /** Dónde está cada entidad (unidades del dibujo), para enfocar las que el visor no sabe ubicar. */
  centrosDwg: { a: ReadonlyMap<string, { x: number; y: number }>; b: ReadonlyMap<string, { x: number; y: number }> } | null;
}) {
  const [vista, setVista] = useState<'a' | 'b'>(() => ladoDelConcepto(piezas, foco) ?? 'b');
  const [enfoque, setEnfoque] = useState<number | null>(null);
  const [camara, setCamara] = useState<{ n: number; vista: 'seleccion' } | null>(null);
  const [filaSel, setFilaSel] = useState<number | null>(null);
  const [pendiente, setPendiente] = useState<number | null>(null);
  const filas = useRef<(HTMLTableRowElement | null)[]>([]);
  const contador = useRef(0);
  const colores = useMemo(() => coloresDeLaVista(piezas, vista), [piezas, vista]);
  const tonos = useMemo(() => Array.from(new Set(colores.values())), [colores]);
  // DWG: las capas y los bloques que cambiaron (por nombre), tal como existen en el lado que se mira.
  const resaltadoDwg = useMemo(() => {
    const bloques = new Set<string>();
    const capas = new Set<string>();
    for (const p of piezas) {
      const pz = vista === 'a' ? p.a : p.b;
      if (!pz) continue;
      if (pz.clase === 'bloque') bloques.add(pz.grupo);
      else if (pz.clase === 'capa') capas.add(pz.grupo);
    }
    return { bloques: [...bloques], capas: [...capas] };
  }, [piezas, vista]);
  // DWG: lo que se está mirando. `focoDwg` = UN concepto o UN elemento de la lista (solo lo suyo va resaltado, el resto
  // se atenúa); sin foco, todo lo que cambió. `soloVer` = ver el plano sin resaltar.
  // Si ya viene un concepto elegido (se tocó en la tabla antes de que se abriera esta pestaña), se arranca con él: así el
  // visor pinta UNA vez (repintar todo el dibujo es caro) y no primero «todo lo que cambió» y luego el concepto.
  const [focoDwg, setFocoDwg] = useState<{ titulo: string; capas: string[]; bloques: string[] } | null>(
    foco ? { titulo: foco.titulo, capas: foco.capas, bloques: foco.bloques } : null,
  );
  const [soloVer, setSoloVer] = useState(false);
  const [enfoqueDwg, setEnfoqueDwg] = useState<{ n: number; capas: string[]; bloques: string[]; handles?: string[] } | null>(
    foco ? { n: 1, capas: foco.capas, bloques: foco.bloques } : null,
  );
  const contadorDwg = useRef(foco ? 1 : 0);
  const focoYaAplicado = useRef<number | null>(foco?.n ?? null);
  // Los roles del lado que se mira (nada si el dibujo parece movido entero: ahí no se puede marcar pieza por pieza).
  const rolesLado = diffDwg && !diffDwg.desplazado ? (vista === 'a' ? diffDwg.rolesA : diffDwg.rolesB) : null;
  const handlesConRol = useMemo(() => (rolesLado ? [...rolesLado.keys()] : undefined), [rolesLado]);

  // Un concepto elegido en la tabla de presupuesto: solo se resalta lo suyo y la cámara va ahí.
  useEffect(() => {
    if (!foco || focoYaAplicado.current === foco.n) return;
    focoYaAplicado.current = foco.n;
    setSoloVer(false);
    const lado = ladoDelConcepto(piezas, foco);
    if (lado) setVista(lado);
    setFocoDwg({ titulo: foco.titulo, capas: foco.capas, bloques: foco.bloques });
    setEnfoqueDwg({ n: ++contadorDwg.current, capas: foco.capas, bloques: foco.bloques });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foco?.n]);
  const quitarFocoDwg = () => {
    setFocoDwg(null);
    setSoloVer(false);
    setEnfoqueDwg({ n: ++contadorDwg.current, capas: handlesConRol ? [] : resaltadoDwg.capas, bloques: handlesConRol ? [] : resaltadoDwg.bloques, handles: handlesConRol });
    onQuitarFoco();
  };
  // Lo que se pinta en el visor del dibujo: null = colores del plano tal cual.
  // (memoizado: el visor repinta cada vez que esto cambia de referencia). Ámbar = «cambió»; la lista dice cómo.
  const resaltarCambioDwg = useMemo(
    () =>
      soloVer || rolesLado
        ? null
        : { ...(focoDwg ? { capas: focoDwg.capas, bloques: focoDwg.bloques } : { capas: resaltadoDwg.capas, bloques: resaltadoDwg.bloques }), rol: 'modified' as const },
    [soloVer, focoDwg, resaltadoDwg, rolesLado],
  );
  // El primer encuadre: todo lo que cambió (lo pide el visor al terminar de abrir el plano).
  const enfoqueInicialDwg = useMemo(
    () => ({ n: 0, capas: handlesConRol ? [] : resaltadoDwg.capas, bloques: handlesConRol ? [] : resaltadoDwg.bloques, handles: handlesConRol }),
    [resaltadoDwg, handlesConRol],
  );
  const quitadas = piezas.filter((p) => p.tipo === 'quitada').length;
  const agregadas = piezas.filter((p) => p.tipo === 'agregada').length;

  const enfocar = (eid: number) => {
    setEnfoque(eid);
    setCamara({ n: ++contador.current, vista: 'seleccion' });
  };
  const cambiarVista = (v: 'a' | 'b') => {
    setSoloVer(false);
    if (v === vista) return;
    setEnfoque(null);
    setCamara(null);
    setVista(v);
  };
  const elegirFila = (i: number) => {
    setFilaSel(i);
    const p = piezas[i]!;
    const aqui = piezaDeFila(p, vista);
    if (aqui != null) {
      enfocar(aqui);
      return;
    }
    // La pieza no existe en esta vista (una nueva vista en «Antes», una quitada vista en «Ahora»): se pasa a la otra
    // y el foco queda pendiente hasta que cargue ese modelo.
    const otra = vista === 'a' ? 'b' : 'a';
    const alla = piezaDeFila(p, otra);
    if (alla != null) {
      setPendiente(alla);
      cambiarVista(otra);
    }
  };
  const alCargar = () => {
    if (pendiente != null) {
      enfocar(pendiente);
      setPendiente(null);
    }
  };
  const alElegirEnVisor = (eid: number | null) => {
    setEnfoque(eid);
    if (eid == null) return;
    const i = filaDePieza(piezas, vista, eid);
    if (i >= 0) {
      setFilaSel(i);
      filas.current[i]?.scrollIntoView({ block: 'nearest' });
    }
  };

  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
      {(
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Button size="small" appearance={vista === 'a' ? 'primary' : 'secondary'} onClick={() => cambiarVista('a')}>
          Antes
        </Button>
        <Button size="small" appearance={vista === 'b' ? 'primary' : 'secondary'} onClick={() => cambiarVista('b')}>
          Ahora
        </Button>
        <span style={estilos.sub} title={vista === 'a' ? nombreAnterior : nombreNuevo}>
          Viendo: «{corto(vista === 'a' ? nombreAnterior : nombreNuevo, 38)}»
        </span>
        {formato === 'dwg' && rolesLado && !soloVer && (
          <>
            <Pastilla texto="Nuevo" tono="ok" />
            <Pastilla texto="Ya no está" tono="bad" />
            <Pastilla texto="Cambió" tono="warn" />
          </>
        )}
        {formato === 'dwg' && diffDwg && !diffDwg.desplazado && (
          <span style={estilos.sub}>
            {diffDwg.nuevas} {diffDwg.nuevas === 1 ? 'nueva' : 'nuevas'} · {diffDwg.quitadas} {diffDwg.quitadas === 1 ? 'quitada' : 'quitadas'} · {diffDwg.modificadas} {diffDwg.modificadas === 1 ? 'cambió' : 'cambiaron'}
          </span>
        )}
        {formato === 'dwg' && diffDwg?.desplazado && (
          <span style={{ ...estilos.sub, color: 'var(--warn)' }}>
            Se parece muy poco al de tu hoja (otro plano, o movido o a otra escala): no puedo marcar pieza por pieza. Te muestro las capas que cambiaron.
          </span>
        )}
        {formato === 'dwg' && (
          <Button size="small" appearance="subtle" onClick={() => setSoloVer((v) => !v)}>
            {soloVer ? 'Resaltar lo que cambió' : 'Ver sin resaltar'}
          </Button>
        )}
        {tonos.map((k) => (
          <Pastilla key={k} texto={LEYENDA_TONO[k]} tono={k} />
        ))}
        <span style={estilos.sub}>
          {vista === 'b' && quitadas > 0
            ? `${quitadas} ${quitadas === 1 ? (formato === 'dwg' ? 'elemento ya no está' : 'pieza ya no está') : formato === 'dwg' ? 'elementos ya no están' : 'piezas ya no están'}: míralos en «Antes».`
            : vista === 'a' && agregadas > 0
              ? `${agregadas} ${agregadas === 1 ? (formato === 'dwg' ? 'elemento es nuevo' : 'pieza es nueva') : formato === 'dwg' ? 'elementos son nuevos' : 'piezas son nuevas'}: míralos en «Ahora».`
              : formato === 'dwg'
                ? soloVer
                  ? 'Estás viendo el plano sin resaltar.'
                  : focoDwg
                    ? `Viendo: ${focoDwg.titulo}. Lo demás se atenúa.`
                    : rolesLado
                      ? 'Verde: nuevo · rojo: ya no está · ámbar: cambió. Lo demás se atenúa. Toca una fila para verla sola.'
                      : 'En ámbar, las capas y bloques que cambiaron (una capa se ve completa en todas las plantas); la lista dice cómo. Toca una fila para verla sola.'
                : 'Lo que no cambió se ve tenue.'}
        </span>
        {formato === 'dwg' && focoDwg && (
          <Button size="small" appearance="subtle" onClick={quitarFocoDwg}>
            Ver todo lo que cambió
          </Button>
        )}
      </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 12 }}>
        {formato === 'ifc' && (
        <div
          style={{
            height: 360,
            minWidth: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--border-subtle)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <IfcViewer3D
            key={vista}
            projectId={projectId}
            fileId={vista === 'a' ? fileAnteriorId : fileNuevoId}
            instColores={soloVer ? new Map() : colores}
            instEnfoque={enfoque}
            ordenCamara={camara}
            onPickPieza={(eid) => alElegirEnVisor(eid)}
            onInventario={alCargar}
          />
        </div>
        )}
        {formato === 'dwg' && (
          <div style={{ height: 360, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column', border: '1px solid var(--border-subtle)', borderRadius: 6, overflow: 'hidden' }}>
            {calculandoDiff ? (
              <div style={{ margin: 'auto', fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Spinner size="extra-tiny" />
                Comparando los dos dibujos…
              </div>
            ) : (
            <PlanoViewerMlightcad
              key={vista}
              projectId={projectId}
              fileId={vista === 'a' ? fileAnteriorId : fileNuevoId}
              renglones={SIN_RENGLONES}
              cargarDxf={bytesDwg ? () => Promise.resolve(bytesDwg[vista].slice(0)) : undefined}
              comparacionPorEntidad={rolesLado}
              filtroConcepto={focoDwg ? { capas: focoDwg.capas, bloques: focoDwg.bloques } : null}
              centrosExtra={centrosDwg ? centrosDwg[vista] : null}
              resaltarCambio={resaltarCambioDwg}
              enfocar={enfoqueDwg ?? enfoqueInicialDwg}
              onSeleccion={() => {}}
              sinControles
            />
            )}
          </div>
        )}
        <div style={{ maxHeight: 360, minWidth: 0, flexShrink: 0, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <tbody>
              {piezas.map((p, i) => {
                const pz = (p.b ?? p.a)!;
                const pas = PASTILLA_PIEZA[p.tipo];
                return (
                  <tr
                    key={i}
                    ref={(el) => {
                      filas.current[i] = el;
                    }}
                    onClick={() => {
                      if (formato === 'ifc') {
                        elegirFila(i);
                        return;
                      }
                      // DWG: esa capa o ese bloque solo, en el dibujo.
                      setFilaSel(i);
                      setSoloVer(false);
                      // Un elemento que ya no está solo existe en «Antes»; uno nuevo, solo en «Ahora»: se pasa a ese lado.
                      const ladoActual = vista === 'a' ? p.a : p.b;
                      const lado = ladoActual ?? p.a ?? p.b;
                      if (!lado) return;
                      if (!ladoActual) setVista(vista === 'a' ? 'b' : 'a');
                      const capas = lado.clase === 'capa' ? [lado.grupo] : [];
                      const bloques = lado.clase === 'bloque' ? [lado.grupo] : [];
                      setFocoDwg({ titulo: lado.nombre ?? lado.grupo, capas, bloques });
                      setEnfoqueDwg({ n: ++contadorDwg.current, capas, bloques });
                    }}
                    style={{ cursor: 'pointer', background: filaSel === i ? 'var(--bg-raised)' : undefined }}
                    title={formato === 'dwg' ? 'Toca para verlo solo en el dibujo' : undefined}
                  >
                    <td style={estilos.td}>
                      <div style={{ color: 'var(--text-primary)' }}>
                        {pz.nombre ?? pz.grupo}
                        {formato === 'ifc' && pz.nivel ? <span style={{ color: 'var(--text-tertiary)' }}> · {pz.nivel}</span> : null}
                      </div>
                      <div style={estilos.sub}>{p.detalle}</div>
                    </td>
                    <td style={{ ...estilos.td, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Pastilla texto={pas.texto} tono={pas.tono} />
                        {p.dudoso && <Pastilla texto={NO_ES_SEGURO} tono="warn" />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{titulo}</div>
      {children}
    </div>
  );
}

function Cifra({ marca, valor, sub, color, colorMarca }: { marca: string; valor: string; sub?: string; color?: string; colorMarca?: string }) {
  return (
    <div style={{ background: 'var(--bg-raised)', borderRadius: 6, padding: '8px 10px', borderTop: colorMarca ? `3px solid ${colorMarca}` : undefined }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, color: colorMarca ?? 'var(--text-tertiary)' }}>{marca}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color ?? 'var(--text-primary)' }}>{valor}</div>
      {sub && <div style={{ fontSize: 12, color: color ?? 'var(--text-secondary)' }}>{sub}</div>}
    </div>
  );
}

const estilos = {
  th: {
    position: 'sticky' as const,
    top: 0,
    background: 'var(--bg-raised)',
    textAlign: 'left' as const,
    padding: '6px 8px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  td: { padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' as const },
  num: { textAlign: 'right' as const, whiteSpace: 'nowrap' as const },
  sub: { fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 },
  lista: { margin: '2px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column' as const, gap: 3 },
  guia: { fontSize: 13, lineHeight: 1.45, background: 'var(--bg-raised)', borderLeft: '3px solid var(--accent)', borderRadius: 6, padding: '8px 12px' },
  aviso: { fontSize: 13, color: 'var(--warn)', background: 'var(--warn-weak)', borderRadius: 6, padding: '6px 10px' },
};
